// frontend/src/components/CRSFormSections/ResultDisplay.js
import React from 'react';
import { Box, Typography, Divider } from '@mui/material'; // Import Divider

function ResultDisplay({ crsResult, showSpouseFactors }) {
  // Guard clause: Don't render if no results are available
  if (!crsResult) {
    return null;
  }

  // Helper to check if spouse factors should be displayed
  // Shows if spouse points > 0 OR if the form indicates spouse factors were relevant
  const shouldDisplaySpouseSection = crsResult.spousePoints.total > 0 || showSpouseFactors;

  return (
    // Main container styling (matches CRSSuggestions margin top on small screens)
    <Box sx={{
      mt: { xs: 2, md: 0 },
      p: 3,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      backgroundColor: 'background.paper',
      // height: '100%' // <-- REMOVE OR COMMENT OUT THIS LINE
    }}>
      {/* Top Title - Corrected grammar and flow */}
      <Typography variant="h5" component="p" textAlign="center" gutterBottom>
        Estimated CRS Score: {crsResult.grandTotal}
      </Typography>
      <Typography variant="subtitle1" component="p" textAlign="center" gutterBottom sx={{ mb: 3 }}>
         Score Breakdown
      </Typography>

      {/* Breakdown Grid */}
      <Box sx={{ display: 'grid', gap: 1.5 }}> {/* Reduced gap slightly if needed */}

        {/* --- Core Factors --- */}
        <Typography variant="h6" component="p">Core / Human Capital Factors</Typography>
        <Typography> - Age: {crsResult.corePoints.age}</Typography>
        <Typography> - Level of Education: {crsResult.corePoints.education}</Typography>
        {/* Combined Language Score */}
        <Typography> - Official Languages Proficiency: {crsResult.corePoints.firstLanguage + crsResult.corePoints.secondLanguage}</Typography>
        {/* Indented breakdown */}
        <Typography sx={{ pl: 2 }}> - First Official Language: {crsResult.corePoints.firstLanguage}</Typography>
        <Typography sx={{ pl: 2 }}> - Second Official Language: {crsResult.corePoints.secondLanguage}</Typography>
        <Typography> - Canadian Work Experience: {crsResult.corePoints.canadianWorkExp}</Typography>
        {/* Subtotal */}
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 1 }}>Subtotal Core: {crsResult.corePoints.total}</Typography>

        {/* --- Spouse Factors (Conditional) --- */}
        {shouldDisplaySpouseSection && ( // Use the helper variable
          <>
            <Divider sx={{ my: 2 }} /> {/* Use MUI Divider */}
            <Typography variant="h6" component="p">Spouse Factors</Typography>
            <Typography> - Level of Education: {crsResult.spousePoints.education}</Typography>
            <Typography> - First Official Language: {crsResult.spousePoints.firstLanguage}</Typography>
            <Typography> - Canadian Work Experience: {crsResult.spousePoints.canadianWorkExp}</Typography>
            {/* Subtotal */}
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 1 }}>Subtotal Spouse: {crsResult.spousePoints.total}</Typography>
          </>
        )}

        {/* --- Skill Transferability Factors --- */}
        <Divider sx={{ my: 2 }} /> {/* Use MUI Divider */}
        <Typography variant="h6" component="p">Skill Transferability Factors</Typography>
        {/* Combined Education points */}
        <Typography> - Education & Language/Canadian Exp: {crsResult.skillTransferabilityPoints.educationLanguage + crsResult.skillTransferabilityPoints.educationCanadianWorkExp}</Typography>
        <Typography sx={{ pl: 2 }}> - (A) Edu + Language Proficiency: {crsResult.skillTransferabilityPoints.educationLanguage}</Typography>
        <Typography sx={{ pl: 2 }}> - (B) Edu + Canadian Work Exp: {crsResult.skillTransferabilityPoints.educationCanadianWorkExp}</Typography>
        {/* Combined Foreign Work points */}
        <Typography> - Foreign Work Exp & Language/Canadian Exp: {crsResult.skillTransferabilityPoints.foreignWorkExpLanguage + crsResult.skillTransferabilityPoints.foreignWorkExpCanadian}</Typography>
        <Typography sx={{ pl: 2 }}> - (C) Foreign Work Exp + Language Proficiency: {crsResult.skillTransferabilityPoints.foreignWorkExpLanguage}</Typography>
        <Typography sx={{ pl: 2 }}> - (D) Foreign Work Exp + Canadian Work Exp: {crsResult.skillTransferabilityPoints.foreignWorkExpCanadian}</Typography>
        <Typography> - Certificate of Qualification: {crsResult.skillTransferabilityPoints.certificateQualification}</Typography>
        {/* Subtotal */}
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 1 }}>Subtotal Skills: {crsResult.skillTransferabilityPoints.total}</Typography>

        {/* --- Bonus Points --- */}
        <Divider sx={{ my: 2 }} /> {/* Use MUI Divider */}
        <Typography variant="h6" component="p">Additional Points</Typography>
        <Typography> - Provincial Nomination: {crsResult.bonusPoints.provincialNomination}</Typography>
        <Typography> - Job Offer: {crsResult.bonusPoints.jobOffer}</Typography>
        <Typography> - Study in Canada: {crsResult.bonusPoints.canadianStudy}</Typography>
        <Typography> - Sibling in Canada: {crsResult.bonusPoints.sibling}</Typography>
        <Typography> - French Language Skills: {crsResult.bonusPoints.frenchSkills}</Typography>
        {/* Subtotal */}
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 1 }}>Subtotal Bonus: {crsResult.bonusPoints.total}</Typography>

        {/* --- Grand Total --- */}
        <Divider sx={{ my: 2 }} /> {/* Use MUI Divider */}
        <Typography variant="h5" component="p" textAlign="center" sx={{ mt: 2 }}>
          Grand Total: <Typography variant="h4" component="span" color="primary" sx={{ fontWeight: 'medium' }}>{crsResult.grandTotal}</Typography>
        </Typography>
      </Box>
    </Box>
  );
}

export default ResultDisplay;